import { useMemo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { ChevronLeft, ChevronRight, Dumbbell, Flame, Footprints, Plus, Ruler, Timer } from 'lucide-react';
import { addDays, format, isSameMonth, isToday, startOfWeek } from 'date-fns';
import { enUS, nb } from 'date-fns/locale';
import { useLanguage } from '../../contexts/LanguageContext';
import { useRunningWorkoutCpPreview } from '../../hooks/useRunningWorkoutCpPreview';
import { acwrToHeatmapColor } from '../../utils/acwrHeatmapColor';
import { computeRunningWorkoutEditorSummary } from '../../utils/runningWorkoutEditorSummary';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import RunningWorkoutViz from '../training/RunningWorkoutViz';
import PlannerPlannedChip from './PlannerPlannedChip';
import {
  filterPlannerDayPlannedForDisplay,
  formatDayKey,
  getPlannerSplits,
  monthGridBounds,
  startOfMonth,
  sufferScoreTextClass,
} from './plannerUtils';

function DroppableDay({ day, children, className }) {
  const key = formatDayKey(day);
  const { setNodeRef, isOver } = useDroppable({
    id: `day-${key}`,
    data: { type: 'day', dayKey: key },
  });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'group relative flex h-full min-h-0 min-w-0 flex-col overflow-hidden border border-border/70 bg-background/80 p-1',
        isOver && 'bg-accent/30 ring-1 ring-primary/40',
        className
      )}
    >
      {children}
    </div>
  );
}

function formatMovingTimeShort(sec) {
  if (sec == null || !Number.isFinite(Number(sec)) || Number(sec) <= 0) return null;
  const m = Math.round(Number(sec) / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h}h${mm}m` : `${h}h`;
}

function formatKmFromM(meters) {
  if (meters == null || !Number.isFinite(Number(meters)) || Number(meters) <= 0) return null;
  return `${(Number(meters) / 1000).toFixed(1)}`;
}

export default function PlannerMonthGrid({
  visibleMonth,
  onPrevMonth,
  onNextMonth,
  plannedByDay,
  activityByDay,
  acwrByDay,
  onTimeChange,
  onEditStructure,
  onEditActivityExercise,
  onRequestDelete,
  onActivityPeek,
  onDayCombinedSummary,
  onOpenLinkedActivity,
  onRequestAddExercise,
}) {
  const { t, language } = useLanguage();
  const locale = language === 'no' ? nb : enUS;

  const { days } = useMemo(() => monthGridBounds(visibleMonth), [visibleMonth]);

  const { thresholdPaceSecPerKm, distanceUnits, paceEstimateOpts } = useRunningWorkoutCpPreview([], t);
  const distanceUnitsNorm = distanceUnits === 'miles' ? 'miles' : 'km';

  const plannedDaySummaries = useMemo(() => {
    const out = {};
    for (const key of Object.keys(plannedByDay)) {
      const list = plannedByDay[key] || [];
      const allSplits = list.flatMap((item) => getPlannerSplits(item) || []);
      if (allSplits.length === 0) continue;
      out[key] = computeRunningWorkoutEditorSummary(
        allSplits,
        thresholdPaceSecPerKm,
        distanceUnitsNorm,
        t,
        paceEstimateOpts
      );
    }
    return out;
  }, [plannedByDay, thresholdPaceSecPerKm, distanceUnitsNorm, t, paceEstimateOpts]);

  const weekdayLabels = useMemo(() => {
    const monday = startOfWeek(startOfMonth(visibleMonth), { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => format(addDays(monday, i), 'EEE', { locale }));
  }, [visibleMonth, locale]);

  const calendarRowCount = Math.ceil(days.length / 7);

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-l border-border bg-card/30">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-3 py-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={onPrevMonth}
          aria-label={t('trainingLog.plannerPrevMonth')}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h2 className="min-w-0 truncate text-center text-sm font-semibold text-foreground">
          {format(visibleMonth, 'LLLL yyyy', { locale })}
        </h2>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={onNextMonth}
          aria-label={t('trainingLog.plannerNextMonth')}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <p className="shrink-0 px-3 py-1 text-[10px] text-muted-foreground">{t('trainingLog.plannerDropHint')}</p>
      <div className="grid shrink-0 grid-cols-7 border-b border-border bg-muted/40 px-1 py-1">
        {weekdayLabels.map((w) => (
          <div
            key={w}
            className="text-center text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
          >
            {w}
          </div>
        ))}
      </div>
      <div
        className="grid min-h-0 flex-1 grid-cols-7 gap-px overflow-hidden bg-border p-px"
        style={{ gridTemplateRows: `repeat(${calendarRowCount}, minmax(0, 1fr))` }}
      >
        {days.map((day) => {
          const key = formatDayKey(day);
          const inMonth = isSameMonth(day, visibleMonth);
          const listRaw = plannedByDay[key] || [];
          const act = activityByDay[key];
          const list = filterPlannerDayPlannedForDisplay(listRaw, act);
          const hasActivity = !!(act?.stravaActivityIds?.length);
          const peekId = hasActivity ? act.stravaActivityIds[0] : null;
          const today = isToday(day);
          const acwr = acwrByDay?.get?.(key) ?? acwrByDay?.[key];
          const durStr = formatMovingTimeShort(act?.totalMovingSec);
          const kmStr = formatKmFromM(act?.totalDistanceM);
          const suffer = act?.maxSufferScore;

          const plannedSum = plannedDaySummaries[key];
          const hasPlannedRunStats =
            !hasActivity &&
            plannedSum &&
            (plannedSum.timeLabel !== '—' ||
              plannedSum.totalMeters > 0 ||
              (plannedSum.sufferScoreEstimate != null && Number.isFinite(plannedSum.sufferScoreEstimate)));
          const pDurStr = hasPlannedRunStats ? formatMovingTimeShort(plannedSum.totalSec) : null;
          const pDistanceLabel =
            hasPlannedRunStats && plannedSum.totalMeters > 0 && plannedSum.distanceLabel && plannedSum.distanceLabel !== '—'
              ? plannedSum.distanceLabel
              : null;
          const pSuffer =
            hasPlannedRunStats && plannedSum.sufferScoreEstimate != null && Number.isFinite(plannedSum.sufferScoreEstimate)
              ? plannedSum.sufferScoreEstimate
              : null;

          return (
            <DroppableDay key={key} day={day} className={cn(!inMonth && 'opacity-40')}>
              {typeof onRequestAddExercise === 'function' && (
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className="absolute bottom-0.5 right-0.5 z-[2] h-6 w-6 shrink-0 opacity-0 shadow-sm transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
                  aria-label={t('trainingLog.plannerAddWorkoutAria')}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRequestAddExercise(key);
                  }}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              )}
              <div className="mb-0.5 flex shrink-0 items-start justify-between gap-0.5">
                <span
                  className={cn(
                    'shrink-0 text-[11px] font-semibold tabular-nums',
                    today ? 'text-primary' : 'text-foreground'
                  )}
                >
                  {format(day, 'd')}
                </span>
                <div className="flex min-w-0 flex-1 flex-col items-end gap-0.5">
                  {hasActivity && (
                    <div className="flex max-w-full flex-wrap items-center justify-end gap-x-1 gap-y-0 text-[9px] leading-none text-muted-foreground">
                      {!!durStr && (
                        <span className="inline-flex items-center gap-0.5 tabular-nums" title={t('trainingLog.plannerStatDuration')}>
                          <Timer className="h-2.5 w-2.5 shrink-0 opacity-80" aria-hidden />
                          {durStr}
                        </span>
                      )}
                      {!!kmStr && (
                        <span className="inline-flex items-center gap-0.5 tabular-nums" title={t('trainingLog.plannerStatDistance')}>
                          <Ruler className="h-2.5 w-2.5 shrink-0 opacity-80" aria-hidden />
                          {kmStr}
                          {t('trainingLog.plannerStatKmSuffix')}
                        </span>
                      )}
                      {suffer != null && Number.isFinite(Number(suffer)) && (
                        <span
                          className={cn('inline-flex items-center gap-0.5 font-medium tabular-nums', sufferScoreTextClass(suffer))}
                          title={t('trainingLog.plannerStatSuffer')}
                        >
                          <Flame className="h-2.5 w-2.5 shrink-0 opacity-90" aria-hidden />
                          {suffer}
                        </span>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 shrink-0 text-muted-foreground hover:text-primary"
                        aria-label={t('trainingLog.plannerOpenActivity')}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (act.stravaActivityIds.length > 1 && onDayCombinedSummary) {
                            onDayCombinedSummary(key);
                          } else if (peekId) {
                            onActivityPeek?.(peekId);
                          }
                        }}
                      >
                        <Footprints className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                  {hasPlannedRunStats && (
                    <div
                      className="flex max-w-full flex-wrap items-center justify-end gap-x-1 gap-y-0 text-[9px] leading-none text-muted-foreground"
                      title={t('trainingLog.plannerPlannedDayStatsTooltip')}
                    >
                      {!!pDurStr && (
                        <span
                          className="inline-flex items-center gap-0.5 tabular-nums"
                          title={t('trainingLog.plannerStatDurationPlanned')}
                        >
                          <Timer className="h-2.5 w-2.5 shrink-0 opacity-80" aria-hidden />
                          {pDurStr}
                        </span>
                      )}
                      {!!pDistanceLabel && (
                        <span
                          className="inline-flex items-center gap-0.5 tabular-nums"
                          title={t('trainingLog.plannerStatDistancePlanned')}
                        >
                          <Ruler className="h-2.5 w-2.5 shrink-0 opacity-80" aria-hidden />
                          {pDistanceLabel}
                        </span>
                      )}
                      {pSuffer != null && (
                        <span
                          className={cn('inline-flex items-center gap-0.5 font-medium tabular-nums', sufferScoreTextClass(pSuffer))}
                          title={t('trainingLog.plannerStatSufferPlanned')}
                        >
                          <Flame className="h-2.5 w-2.5 shrink-0 opacity-90" aria-hidden />
                          {pSuffer}
                        </span>
                      )}
                    </div>
                  )}
                  {today && !hasActivity && acwr != null && Number.isFinite(Number(acwr)) && (
                    <span
                      className="rounded px-1 py-0.5 text-[9px] font-semibold tabular-nums text-primary-foreground"
                      style={{ backgroundColor: acwrToHeatmapColor(acwr) }}
                      title={t('trainingLog.plannerAcwrTooltip', { value: String(acwr) })}
                    >
                      ACWR {acwr}
                    </span>
                  )}
                </div>
              </div>
              {(() => {
                const withSplits = (act?.stravaActivities || []).filter(
                  (sa) => Array.isArray(sa.previewSplits) && sa.previewSplits.length > 0
                );
                if (withSplits.length === 0) return null;
                return (
                  <div className="mb-0.5 w-full shrink-0 space-y-0.5 px-0.5">
                    {withSplits.map((sa) => {
                      const clickable = typeof onActivityPeek === 'function';
                      return (
                        <div
                          key={sa.id}
                          role={clickable ? 'button' : undefined}
                          tabIndex={clickable ? 0 : undefined}
                          className={cn(
                            'flex w-full min-w-0 items-center gap-0.5',
                            clickable && 'cursor-pointer rounded hover:ring-1 hover:ring-primary/50'
                          )}
                          title={t('trainingLog.plannerStrydWorkoutStructureHint')}
                          onClick={clickable ? (e) => { e.stopPropagation(); onActivityPeek(sa.id); } : undefined}
                          onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onActivityPeek(sa.id); } } : undefined}
                        >
                          <Dumbbell className="h-3 w-3 shrink-0 text-primary" aria-hidden />
                          <RunningWorkoutViz splits={sa.previewSplits} heightPx={18} className="min-w-0 flex-1" />
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              <div
                className={cn(
                  'flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto',
                  typeof onRequestAddExercise === 'function' && 'pb-7'
                )}
              >
                {list.map((item) => (
                  <PlannerPlannedChip
                    key={item.id}
                    item={item}
                    dayKey={key}
                    onTimeChange={onTimeChange}
                    onEditStructure={onEditStructure}
                    onRequestDelete={onRequestDelete}
                    onOpenLinkedActivity={onOpenLinkedActivity}
                  />
                ))}
              </div>
            </DroppableDay>
          );
        })}
      </div>
    </div>
  );
}
